import { useState, useEffect, useRef } from "react";
import { FaRegUser } from "react-icons/fa"
import { MdMailOutline,MdOutlineCameraAlt } from "react-icons/md";
import {  IoCallOutline,IoSaveOutline ,IoCloseSharp } from "react-icons/io5";
import { GoShieldCheck ,GoXCircle,GoTrash } from "react-icons/go";
import { FaRegEdit,FaRegClock } from "react-icons/fa";
import { CiCircleCheck } from "react-icons/ci";
import { IoCloudUploadOutline } from "react-icons/io5";
import { strings } from "../../../strings/strings";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { apiCall } from "../../../api/fetchClient";
import { GET_PROFILE_URL } from "../../../api/url";
import { toast } from "react-toastify";

const ProfilePage = () => {
  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const imageMenuRef = useRef(null);
  const hasFetchedProfile = useRef(false);
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    mobileCode: "",
    mobileNumber: "",
    profileImage: null,
    isActive: true,
    lastLogin: "",
  });

  const [editedData, setEditedData] = useState({ ...profileData });

  useEffect(() => {
    if (hasFetchedProfile.current) return;
    hasFetchedProfile.current = true;

    const fetchProfile = async () => {
      try {
        const response = await apiCall(GET_PROFILE_URL, "GET");
        if (!!response?.success) {
          setProfileData(response?.results);
          setEditedData(response?.results);
          setImagePreview(response?.results?.profileImage);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        imageMenuRef.current &&
        !imageMenuRef.current.contains(event.target)
      ) {
        setShowImageMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({ ...profileData });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({ ...profileData });
    setImagePreview(profileData.profileImage);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      let hasChanges = false;

      if (editedData.firstName !== profileData.firstName) {
        formData.append("firstName", editedData.firstName);
        hasChanges = true;
      }

      if (editedData.lastName !== profileData.lastName) {
        formData.append("lastName", editedData.lastName);
        hasChanges = true;
      }

      if (editedData.profileImage === null && profileData.profileImage !== null) {
        formData.append("profileImage", null);
        hasChanges = true;
      } else if (editedData.profileImage instanceof File) {
        formData.append("profileImage", editedData.profileImage);
        hasChanges = true;
      }

      if (hasChanges) {
        const response = await apiCall(GET_PROFILE_URL, "PUT", formData, true);
        if (!!response?.success) {
          setLoading(false);
          const updatedProfile = response?.results || editedData;
          setProfileData(updatedProfile);
          setImagePreview(updatedProfile?.profileImage || null);
          localStorage.setItem("superAdmin", JSON.stringify(updatedProfile));
          
          // Dispatch custom event for same-tab updates
          window.dispatchEvent(new CustomEvent("profileUpdated"));
          // Also dispatch storage event for cross-tab updates
          window.dispatchEvent(new Event("storage"));
        }
      } else {
        setProfileData({ ...editedData });
        setLoading(false);
      }

      setIsEditing(false);
    } catch (error) {
      setLoading(false);
      toast.error(error?.message || "Failed to update profile");
    }
  };
  const handleChange = (field, value) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setEditedData((prev) => ({
          ...prev,
          profileImage: file,
        }));
      };
      reader.readAsDataURL(file);
    }
    setShowImageMenu(false);
  };

  const handleDeleteImage = () => {
    setImagePreview(null);
    setEditedData((prev) => ({ ...prev, profileImage: null }));
    setShowImageMenu(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatLastLogin = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = () => {
    return `${profileData.firstName?.[0] || ""}${profileData.lastName?.[0] || ""}`.toUpperCase();
  };

  if (loading) {
    return (
      <LoadingSpinner
        message="Loading profile..."
        minHeight="min-h-[70vh]"
      />
    );
  }

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <PageHeader
        title={strings.MY_PROFILE}
        description={strings.PROFILE_DESCRIPTION}
        icon={FaRegUser}
      >
        <div className="flex gap-3">
          {!isEditing ? (
            <button
              onClick={handleEdit}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <FaRegEdit className="w-4 h-4" />
              {strings.EDIT_PROFILE}
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <IoSaveOutline className="w-4 h-4" />
                {strings.SAVE_CHANGES}
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                <IoCloseSharp className="w-4 h-4" />
                {strings.CANCEL}
              </button>
            </>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getInitials()
                  )}
                </div>

                {/* Edit/Delete Menu */}
                {isEditing && (
                  <div className="relative" ref={imageMenuRef}>
                    <button
                      onClick={() => setShowImageMenu(!showImageMenu)}
                      className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full shadow-lg hover:bg-primary/90 transition-colors"
                    >
                      <MdOutlineCameraAlt className="w-4 h-4" />
                    </button>

                    {showImageMenu && (
                      <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-50">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 transition-colors"
                        >
                          <IoCloudUploadOutline className="w-4 h-4" />
                          {strings.UPLOAD_PHOTO}
                        </button>
                        {imagePreview && (
                          <button
                            onClick={handleDeleteImage}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <GoTrash className="w-4 h-4" />
                            {strings.DELETE}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-1">
                {profileData.firstName} {profileData.lastName}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                @{profileData.userName}
              </p>

              {/* Status Badge */}
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full mb-4 ${profileData.isActive
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  }`}
              >
                {profileData.isActive ? (
                  <>
                    <CiCircleCheck className="w-3.5 h-3.5" />
                    {strings.ACTIVE}
                  </>
                ) : (
                  <>
                    <GoXCircle className="w-3.5 h-3.5" />
                    {strings.INACTIVE}
                  </>
                )}
              </span>

              {/* Info Cards */}
              <div className="w-full space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Last Login */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-1">
                    <FaRegClock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase">{strings.LAST_LOGIN}</span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatLastLogin(profileData.lastLogin)}
                  </p>
                </div>

                {/* Role */}
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-1">
                    <GoShieldCheck className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase">{strings.ROLE}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{strings.SUPER_ADMIN_ADMINISTER}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FaRegUser className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{strings.PERSONAL_INFORMATION}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{strings.FIRAT_NAME}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                    placeholder="Enter first name"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {profileData.firstName}
                    </p>
                  </div>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{strings.LAST_NAME}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                    placeholder="Enter last name"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {profileData.lastName}
                    </p>
                  </div>
                )}
              </div>

              {/* Username */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{strings.USERNAME}</label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700/30 rounded-lg border border-gray-300 dark:border-gray-600 opacity-60 cursor-not-allowed">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    @{profileData.userName}
                  </p>
                </div>
                {isEditing && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{strings.USERNAME_CANNOT_BE_CHANGED}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="p-2 bg-primary/10 dark:bg-primary/30 rounded-lg">
                <MdMailOutline className="w-5 h-5 text-primary-dark dark:text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{strings.CONTACT_INFORMATION}</h3>
            </div>

            <div className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{strings.EMAIL}</label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700/30 rounded-lg border border-gray-300 dark:border-gray-600 opacity-60 cursor-not-allowed flex items-center gap-2">
                  <MdMailOutline className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {profileData.email}
                  </p>
                </div>
                {isEditing && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{strings.EMAIL_CANNOT_BE_CHANGED}</p>
                )}
              </div>

              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{strings.MOBILE_NUMBER}</label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700/30 rounded-lg border border-gray-300 dark:border-gray-600 opacity-60 cursor-not-allowed flex items-center gap-2">
                  <IoCallOutline className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {profileData.mobileCode} {profileData.mobileNumber}
                  </p>
                </div>
                {isEditing && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{strings.MOBILE_NUMBER_CANNOT_BE_CHANGED}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
